/**
 * MCP Servers page — Phase 7 of the MCP integration plan.
 *
 * The customer admin's day-2 control plane for integrations the Aton
 * team has registered for the tenant. List view shows one card per
 * MCP with a status badge and the 24 h metrics aggregate; clicking
 * a card switches to the per-MCP detail view with the approved tool
 * catalog and the four destructive actions (Pause, Rotate token,
 * Re-probe, Remove).
 *
 * New-MCP registration and Tier-C approvals are deliberately not on
 * this page — those remain operator-gated in the admin API.
 *
 * Two MSAL variants follow the same pattern as KnowledgePage / Account
 * — `useMsal` is only called inside an MsalProvider, which is mounted
 * only when the portal AAD app is configured.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useMsal } from "@azure/msal-react";
import { acquireIdToken } from "@/auth/msal";
import {
  deleteMCPServer,
  disableMCPServer,
  getMCPServerDetail,
  getMe,
  listMCPServers,
  reprobeMCPServer,
  rotateMCPToken,
  type MCPServerDetail,
  type MCPServerListItem,
  type MCPServerStatus,
  type MeResult,
} from "@/api/portal";
import { Button, Card, Icon, Spinner } from "@/components/ui";
import { useI18n } from "@/lib/i18n";

type IdTokenFetcher = () => Promise<string | null>;

type ActiveAction = "disable" | "delete" | "rotate" | "reprobe" | null;

// ---- helpers ---------------------------------------------------------------

function StatusBadge({ status }: { status: MCPServerStatus }) {
  const { t } = useI18n();
  const tone: Record<MCPServerStatus, string> = {
    pending_iam: "bg-surface-container-highest text-on-surface-variant",
    pending_probe: "bg-surface-container-highest text-on-surface-variant",
    pending_approval: "bg-tertiary-fixed text-on-tertiary-fixed-variant",
    ready: "bg-emerald-100 text-emerald-800",
    disabled: "bg-surface-variant text-on-surface-variant",
    auth_failed: "bg-error-container text-on-error-container",
    degraded: "bg-tertiary-fixed text-on-tertiary-fixed-variant",
  };
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-[12px] font-semibold ${tone[status]}`}
    >
      {t(`mcp.status.${status}`)}
    </span>
  );
}

function Metric({
  label,
  value,
  danger,
}: {
  label: string;
  value: string;
  danger?: boolean;
}) {
  return (
    <div>
      <dt className="text-[11px] font-medium uppercase tracking-wider text-on-surface-variant">
        {label}
      </dt>
      <dd
        className={`mt-0.5 text-body-main font-semibold ${
          danger ? "text-error" : "text-on-surface"
        }`}
      >
        {value}
      </dd>
    </div>
  );
}

function MetricsLine({ server }: { server: MCPServerListItem | MCPServerDetail }) {
  const { t } = useI18n();
  const m = server.metrics_24h;
  if (m.call_count === 0) {
    return (
      <p className="text-label-muted text-on-surface-variant">
        {t("mcp.card.noTraffic")}
      </p>
    );
  }
  const errPct = (m.error_rate * 100).toFixed(1);
  const p95 = m.latency_ms_p95 != null ? `${m.latency_ms_p95.toFixed(0)}ms` : "—";
  return (
    <dl className="flex gap-10">
      <Metric label={t("mcp.metric.calls")} value={String(m.call_count)} />
      <Metric
        label={t("mcp.metric.errors")}
        value={`${errPct}%`}
        danger={m.error_rate >= 0.05}
      />
      <Metric label={t("mcp.metric.latencyP95")} value={p95} />
    </dl>
  );
}

function Notice({
  icon,
  title,
  children,
}: {
  icon: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="rounded-xl border border-outline-variant bg-surface-container p-6">
      <div className="flex gap-4">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-surface-container-highest text-on-surface-variant">
          <Icon name={icon} />
        </span>
        <div>
          <h2 className="text-section-head text-on-surface">{title}</h2>
          <div className="mt-2 text-body-sm text-on-surface-variant">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

function ErrorNotice({ message }: { message: string }) {
  const { t } = useI18n();
  return (
    <Notice icon="error" title={t("error.title")}>
      {message}
    </Notice>
  );
}

function ModalShell({ children }: { children: ReactNode }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{
        backgroundColor: "rgba(29,28,22,0.4)",
        backdropFilter: "blur(2px)",
      }}
    >
      <div className="w-full max-w-lg rounded-xl border border-outline-variant bg-surface p-6 shadow-soft">
        {children}
      </div>
    </div>
  );
}

// ---- list view -------------------------------------------------------------

function ServerCard({
  server,
  onOpen,
}: {
  server: MCPServerListItem;
  onOpen: (id: string) => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(server.mcp_server_id)}
        className="w-full rounded-xl border border-outline-variant bg-surface-container-lowest p-5 text-left transition-colors hover:bg-surface-container-low"
      >
        <div className="flex items-start gap-4">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-surface-container-high text-on-surface-variant">
            <Icon name="dns" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <p
                className="truncate text-section-head text-on-surface"
                title={server.display_name}
              >
                {server.display_name}
              </p>
              <StatusBadge status={server.status} />
            </div>
            <p className="mt-1 text-body-sm text-on-surface-variant">
              {server.description || server.alias}
            </p>
            <div className="mt-4 border-t border-outline-variant pt-3">
              <MetricsLine server={server} />
            </div>
          </div>
        </div>
      </button>
    </li>
  );
}

function EmptyState() {
  const { t } = useI18n();
  return (
    <Notice icon="dns" title={t("mcp.empty.title")}>
      {t("mcp.empty.body")}
    </Notice>
  );
}

function NotOnboardedNotice() {
  const { t } = useI18n();
  return (
    <Notice icon="apartment" title={t("notOnboarded.title")}>
      {t("notOnboarded.body")}
    </Notice>
  );
}

// ---- detail view -----------------------------------------------------------

function ToolRow({ tool }: { tool: MCPServerDetail["tools"][number] }) {
  const { t } = useI18n();
  const [showSchemas, setShowSchemas] = useState(false);
  return (
    <li className="rounded-xl border border-outline-variant bg-surface-container-lowest p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p
            className="truncate text-body-main font-medium text-on-surface"
            title={tool.namespaced_name}
          >
            {tool.tool_name}
          </p>
          <p className="mt-1 line-clamp-2 text-body-sm text-on-surface-variant">
            {tool.description}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-label-muted text-on-surface-variant">
            <span>
              {t("mcp.detail.toolCalls7d", { n: String(tool.call_count_7d) })}
            </span>
            <span aria-hidden>·</span>
            <span>
              {t("mcp.detail.toolErrors7d", { n: String(tool.error_count_7d) })}
            </span>
            {!tool.is_enabled && (
              <>
                <span aria-hidden>·</span>
                <span className="rounded-full bg-surface-variant px-2 py-0.5 text-on-surface-variant">
                  {t("mcp.detail.toolDisabled")}
                </span>
              </>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setShowSchemas((v) => !v)}
          aria-label={tool.tool_name}
          className="rounded-lg border border-outline-variant p-1.5 text-on-surface-variant hover:bg-surface-container-highest"
        >
          <Icon
            name={showSchemas ? "expand_less" : "expand_more"}
            className="text-[20px]"
          />
        </button>
      </div>
      {showSchemas && (
        <div className="mt-3 space-y-3">
          <div>
            <p className="text-label-muted uppercase tracking-wide text-on-surface-variant">
              {t("mcp.detail.inputSchema")}
            </p>
            <pre className="mt-1 max-h-48 overflow-auto rounded-lg bg-surface-container-low p-2 text-label-muted text-on-surface">
              {tool.input_schema}
            </pre>
          </div>
          <div>
            <p className="text-label-muted uppercase tracking-wide text-on-surface-variant">
              {t("mcp.detail.outputSchema")}
            </p>
            <pre className="mt-1 max-h-48 overflow-auto rounded-lg bg-surface-container-low p-2 text-label-muted text-on-surface">
              {tool.output_schema}
            </pre>
          </div>
        </div>
      )}
    </li>
  );
}

function DisableModal({
  onConfirm,
  onCancel,
  busy,
}: {
  onConfirm: () => void;
  onCancel: () => void;
  busy: boolean;
}) {
  const { t } = useI18n();
  return (
    <ModalShell>
      <h2 className="text-section-head text-on-surface">
        {t("mcp.disable.title")}
      </h2>
      <p className="mt-3 text-body-sm text-on-surface-variant">
        {t("mcp.disable.body")}
      </p>
      <div className="mt-6 flex justify-end gap-3">
        <Button variant="secondary" onClick={onCancel} disabled={busy}>
          {t("mcp.action.cancel")}
        </Button>
        <Button onClick={onConfirm} disabled={busy}>
          {t("mcp.disable.confirm")}
        </Button>
      </div>
    </ModalShell>
  );
}

function DeleteModal({
  onConfirm,
  onCancel,
  busy,
}: {
  onConfirm: () => void;
  onCancel: () => void;
  busy: boolean;
}) {
  const { t } = useI18n();
  return (
    <ModalShell>
      <h2 className="text-section-head text-on-surface">
        {t("mcp.delete.title")}
      </h2>
      <p className="mt-3 text-body-sm text-on-surface-variant">
        {t("mcp.delete.body")}
      </p>
      <div className="mt-6 flex justify-end gap-3">
        <Button variant="secondary" onClick={onCancel} disabled={busy}>
          {t("mcp.action.cancel")}
        </Button>
        <Button onClick={onConfirm} disabled={busy}>
          {t("mcp.delete.confirm")}
        </Button>
      </div>
    </ModalShell>
  );
}

function RotateModal({
  onConfirm,
  onCancel,
  busy,
}: {
  onConfirm: (token: string) => void;
  onCancel: () => void;
  busy: boolean;
}) {
  const { t } = useI18n();
  const [value, setValue] = useState("");
  return (
    <ModalShell>
      <h2 className="text-section-head text-on-surface">
        {t("mcp.rotate.title")}
      </h2>
      <p className="mt-3 text-body-sm text-on-surface-variant">
        {t("mcp.rotate.body")}
      </p>
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={t("mcp.rotate.placeholder")}
        rows={4}
        className="mt-4 w-full rounded-lg border border-outline-variant bg-surface-container-lowest p-2 font-mono text-label-muted text-on-surface focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
      />
      <div className="mt-6 flex justify-end gap-3">
        <Button variant="secondary" onClick={onCancel} disabled={busy}>
          {t("mcp.action.cancel")}
        </Button>
        <Button onClick={() => onConfirm(value)} disabled={busy || !value}>
          {t("mcp.rotate.confirm")}
        </Button>
      </div>
    </ModalShell>
  );
}

function ReprobeModal({
  onConfirm,
  onCancel,
  busy,
  result,
}: {
  onConfirm: (urgent: boolean) => void;
  onCancel: () => void;
  busy: boolean;
  result: { a: number; b: number; c: number } | null;
}) {
  const { t } = useI18n();
  const [urgent, setUrgent] = useState(false);
  return (
    <ModalShell>
      <h2 className="text-section-head text-on-surface">
        {t("mcp.reprobe.title")}
      </h2>
      <p className="mt-3 text-body-sm text-on-surface-variant">
        {t("mcp.reprobe.body")}
      </p>
      <label className="mt-4 flex items-center gap-2 text-body-sm text-on-surface">
        <input
          type="checkbox"
          checked={urgent}
          onChange={(e) => setUrgent(e.target.checked)}
          className="accent-primary"
        />
        {t("mcp.reprobe.urgent")}
      </label>
      {result && (
        <p className="mt-4 text-body-sm text-emerald-700">
          {t("mcp.reprobe.done", {
            a: String(result.a),
            b: String(result.b),
            c: String(result.c),
          })}
        </p>
      )}
      <div className="mt-6 flex justify-end gap-3">
        <Button variant="secondary" onClick={onCancel} disabled={busy}>
          {t("mcp.action.cancel")}
        </Button>
        <Button onClick={() => onConfirm(urgent)} disabled={busy}>
          {t("mcp.reprobe.confirm")}
        </Button>
      </div>
    </ModalShell>
  );
}

function ActionButton({
  icon,
  label,
  onClick,
  disabled,
  danger,
}: {
  icon: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  const tone = danger
    ? "border-error-container text-error hover:bg-error-container"
    : "border-outline-variant text-on-surface hover:bg-surface-container-highest";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-button-text transition-colors disabled:opacity-50 ${tone}`}
    >
      <Icon name={icon} className="text-[18px]" />
      {label}
    </button>
  );
}

function DetailView({
  detail,
  onBack,
  fetchIdToken,
  onChanged,
}: {
  detail: MCPServerDetail;
  onBack: () => void;
  fetchIdToken: IdTokenFetcher;
  onChanged: () => void;
}) {
  const { t } = useI18n();
  const [action, setAction] = useState<ActiveAction>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [reprobeResult, setReprobeResult] =
    useState<{ a: number; b: number; c: number } | null>(null);

  const closeModal = useCallback(() => {
    if (busy) return;
    setAction(null);
    setActionError(null);
    setReprobeResult(null);
  }, [busy]);

  const handleDisable = useCallback(async () => {
    setBusy(true);
    setActionError(null);
    try {
      const idToken = await fetchIdToken();
      await disableMCPServer(idToken, detail.mcp_server_id);
      setAction(null);
      onChanged();
    } catch {
      setActionError(t("mcp.error"));
    } finally {
      setBusy(false);
    }
  }, [detail.mcp_server_id, fetchIdToken, onChanged, t]);

  const handleDelete = useCallback(async () => {
    setBusy(true);
    setActionError(null);
    try {
      const idToken = await fetchIdToken();
      await deleteMCPServer(idToken, detail.mcp_server_id);
      setAction(null);
      onBack();
      onChanged();
    } catch {
      setActionError(t("mcp.error"));
    } finally {
      setBusy(false);
    }
  }, [detail.mcp_server_id, fetchIdToken, onBack, onChanged, t]);

  const handleRotate = useCallback(
    async (token: string) => {
      setBusy(true);
      setActionError(null);
      try {
        const idToken = await fetchIdToken();
        await rotateMCPToken(idToken, detail.mcp_server_id, token);
        setAction(null);
        onChanged();
      } catch {
        setActionError(t("mcp.error"));
      } finally {
        setBusy(false);
      }
    },
    [detail.mcp_server_id, fetchIdToken, onChanged, t],
  );

  const handleReprobe = useCallback(
    async (urgent: boolean) => {
      setBusy(true);
      setActionError(null);
      try {
        const idToken = await fetchIdToken();
        const r = await reprobeMCPServer(idToken, detail.mcp_server_id, urgent);
        setReprobeResult({
          a: r.tier_a_count,
          b: r.tier_b_count,
          c: r.tier_c_count,
        });
        onChanged();
      } catch {
        setActionError(t("mcp.error"));
      } finally {
        setBusy(false);
      }
    },
    [detail.mcp_server_id, fetchIdToken, onChanged, t],
  );

  return (
    <div className="space-y-6">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1 text-body-sm font-medium text-on-surface-variant hover:text-on-surface"
      >
        <Icon name="arrow_back" className="text-[18px]" />
        {t("mcp.detail.back")}
      </button>
      <Card>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-page-title text-on-surface">
              {detail.display_name}
            </h1>
            <p className="mt-1 text-body-sm text-on-surface-variant">
              {detail.alias} · {detail.base_url}
            </p>
          </div>
          <StatusBadge status={detail.status} />
        </div>
        <div className="mt-4 border-t border-outline-variant pt-4">
          <MetricsLine server={detail} />
        </div>
        <div className="mt-6 flex flex-wrap gap-2">
          <ActionButton
            icon="pause"
            label={t("mcp.action.disable")}
            onClick={() => setAction("disable")}
            disabled={!detail.is_active}
          />
          <ActionButton
            icon="vpn_key"
            label={t("mcp.action.rotate")}
            onClick={() => setAction("rotate")}
          />
          <ActionButton
            icon="refresh"
            label={t("mcp.action.reprobe")}
            onClick={() => setAction("reprobe")}
          />
          <ActionButton
            icon="delete"
            label={t("mcp.action.delete")}
            onClick={() => setAction("delete")}
            danger
          />
        </div>
        {actionError && (
          <p className="mt-3 text-body-sm text-error">{actionError}</p>
        )}
      </Card>

      <div>
        <h2 className="mb-3 text-section-head text-on-surface">
          {t("mcp.detail.tools")}
        </h2>
        <ul className="space-y-3">
          {detail.tools.map((tool) => (
            <ToolRow key={tool.tool_id} tool={tool} />
          ))}
        </ul>
      </div>

      {action === "disable" && (
        <DisableModal
          onConfirm={handleDisable}
          onCancel={closeModal}
          busy={busy}
        />
      )}
      {action === "delete" && (
        <DeleteModal
          onConfirm={handleDelete}
          onCancel={closeModal}
          busy={busy}
        />
      )}
      {action === "rotate" && (
        <RotateModal
          onConfirm={handleRotate}
          onCancel={closeModal}
          busy={busy}
        />
      )}
      {action === "reprobe" && (
        <ReprobeModal
          onConfirm={handleReprobe}
          onCancel={closeModal}
          busy={busy}
          result={reprobeResult}
        />
      )}
    </div>
  );
}

// ---- main frame ------------------------------------------------------------

function MCPServersFrame({ fetchIdToken }: { fetchIdToken: IdTokenFetcher }) {
  const { t } = useI18n();
  const [me, setMe] = useState<MeResult | null>(null);
  const [list, setList] = useState<MCPServerListItem[] | null>(null);
  const [detail, setDetail] = useState<MCPServerDetail | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const reloadList = useCallback(async () => {
    try {
      const idToken = await fetchIdToken();
      const rows = await listMCPServers(idToken);
      setList(rows);
      setLoadError(false);
    } catch (err) {
      // 403 means unbound tenant — fall through to the not-onboarded
      // notice instead of the generic load-error state.
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes("403")) {
        setMe((prev) =>
          prev && prev.result === "ok" ? prev : { ...(prev as MeResult), result: "not_onboarded" },
        );
        setList([]);
      } else {
        setLoadError(true);
      }
    }
  }, [fetchIdToken]);

  const reloadDetail = useCallback(
    async (id: string) => {
      try {
        const idToken = await fetchIdToken();
        const d = await getMCPServerDetail(idToken, id);
        setDetail(d);
        setLoadError(false);
      } catch {
        setLoadError(true);
      }
    },
    [fetchIdToken],
  );

  // /me drives the not-onboarded gate. The list call also gates on the
  // backend (returns 403 for unbound tenants) but the SPA branches on
  // /me first so we render the same screen as Account/Knowledge.
  useEffect(() => {
    let cancelled = false;
    fetchIdToken()
      .then((idToken) => getMe(idToken))
      .then((result) => {
        if (!cancelled) setMe(result);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchIdToken]);

  useEffect(() => {
    if (me && me.result === "ok") {
      void reloadList();
    }
  }, [me, reloadList]);

  useEffect(() => {
    if (openId) void reloadDetail(openId);
    else setDetail(null);
  }, [openId, reloadDetail]);

  if (loadError) return <ErrorNotice message={t("mcp.error")} />;
  if (!me || !list) {
    return (
      <Card>
        <Spinner />
      </Card>
    );
  }
  if (me.result === "not_onboarded") return <NotOnboardedNotice />;

  if (openId) {
    if (!detail) {
      return (
        <Card>
          <Spinner />
        </Card>
      );
    }
    return (
      <DetailView
        detail={detail}
        onBack={() => setOpenId(null)}
        fetchIdToken={fetchIdToken}
        onChanged={() => {
          if (openId) void reloadDetail(openId);
          void reloadList();
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-page-title text-on-surface">{t("mcp.title")}</h1>
        <p className="mt-1 text-body-sm text-on-surface-variant">
          {t("mcp.subtitle")}
        </p>
      </header>
      {list.length === 0 ? (
        <EmptyState />
      ) : (
        <ul className="space-y-3">
          {list.map((server) => (
            <ServerCard
              key={server.mcp_server_id}
              server={server}
              onOpen={setOpenId}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function MCPServersPageAuthed() {
  const { instance, accounts } = useMsal();
  const fetchIdToken = useCallback(async () => {
    const account = instance.getActiveAccount() ?? accounts[0];
    if (!account) throw new Error("not signed in");
    return acquireIdToken(instance, account);
  }, [instance, accounts]);
  return <MCPServersFrame fetchIdToken={fetchIdToken} />;
}

function MCPServersPageNoAuth() {
  const fetchIdToken = useMemo(() => async () => null, []);
  return <MCPServersFrame fetchIdToken={fetchIdToken} />;
}

export function MCPServersPage({
  authConfigured,
}: {
  authConfigured: boolean;
}) {
  return authConfigured ? <MCPServersPageAuthed /> : <MCPServersPageNoAuth />;
}
